<template>
  <div class="fill-height eledio-theme">
    <!-- Main Loading Overlay -->
    <v-overlay :value="loading && !dataLoaded" class="main-loading-overlay">
      <div class="loading-content text-center">
        <v-progress-circular
          indeterminate
          size="80"
          width="6"
          color="#0073e6"
        ></v-progress-circular>
        <h3 class="mt-4 text-h5">{{ $t('global.loading') }}...</h3>
      </div>
    </v-overlay>

    <!-- Payments for this device are not managed by Eledio: behave as if it doesn't exist
         (no header, no hint that the link is valid) -->
    <NotFoundPage v-if="showNotFound" />

    <!-- Subscription Already Paid Fallback -->
    <div v-else-if="showAlreadyPaidMessage" class="fallback-container">
      <v-row justify="center" class="w-full">
        <v-col cols="12" xs="12" md="8" lg="6" class="w-full">
          <div class="site-header d-flex align-center justify-center mb-6">
            <IXSolveLogo class="logo-full" width="134" height="30" />

            <!-- Language Selector -->
            <div class="language-selector ml-4">
              <LocaleSelect />
            </div>
          </div>

          <v-card elevation="0" class="payment-card pa-8 w-full text-center">
            <v-icon size="80" color="success" class="mb-4">mdi-check-circle</v-icon>
            <h1 class="text-h4 font-weight-bold mb-4">
              {{ $t('global.subscription.alreadyPaid') }}
            </h1>
            <p class="text-body-1 mb-6">
              {{ $t('global.subscription.subscriptionAlreadyActive') }}
            </p>
            <div class="subscription-info-box mb-6">
              <p>
                <strong>{{ $t('global.subscription.deviceName') }}:</strong> {{ deviceName }}
              </p>
              <p>
                <strong>{{ $t('global.subscription.subscriptionExpires') }}</strong>
                {{ formattedExpirationDate }}
              </p>
              <p>
                <strong>{{ $t('global.subscription.daysRemaining') }}:</strong>
                {{ daysUntilExpiration }} {{ $t('global.days') }}
              </p>
            </div>
            <v-btn color="primary" :href="localizedLink" target="_blank" outlined>
              {{ $t('global.moreInfoAboutSubscriptionServices') }}
            </v-btn>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Main Content -->
    <div v-else-if="dataLoaded">
      <v-row justify="center" class="w-full">
        <v-col cols="12" xs="12" md="8" lg="7" class="w-full">
          <div class="site-header d-flex align-center justify-center mb-6">
            <IXSolveLogo class="logo-full" width="134" height="30" />

            <!-- Language Selector -->
            <div class="language-selector ml-4">
              <LocaleSelect />
            </div>
          </div>

          <v-card elevation="0" class="payment-card pa-6 w-full">
            <h1 class="text-h4 font-weight-bold text-center mb-6">
              {{ $t('global.transaction.paymentTitle') }}
            </h1>

            <!-- Subscription Info Section -->
            <div class="subscription-info">
              <v-overlay :value="loading" absolute>
                <v-progress-circular indeterminate size="64"></v-progress-circular>
              </v-overlay>

              <v-card outlined class="mb-5 subscription-details">
                <v-card-title class="subtitle-1 font-weight-bold">
                  {{
                    isSubscriptionExpired
                      ? $t('global.subscription.lastMobileConnectionM2M')
                      : $t('global.subscription.mobileConnectionM2M')
                  }}
                </v-card-title>

                <v-card-text>
                  <!-- Status Badge -->
                  <div
                    v-if="subscriptionData"
                    class="status-badge"
                    :class="{ expired: isSubscriptionExpired }"
                  >
                    {{
                      isSubscriptionExpired
                        ? $t('global.subscription.subscriptionExpired', {
                            days: Math.abs(daysUntilExpiration),
                          })
                        : !isMoreThan30DaysLater(subscriptionData?.expirationDate)
                        ? $t('global.subscription.subscriptionWillEndSoon', {
                            days: daysUntilExpiration,
                          })
                        : $t('global.subscription.activeSubscription')
                    }}
                  </div>

                  <div class="subscription-dates mt-4">
                    <div class="d-flex justify-space-between mb-2">
                      <span class="font-weight-medium">{{
                        $t('global.subscription.subscriptionFrom')
                      }}</span>
                      <span>{{ formattedStartDate }}</span>
                    </div>
                    <div class="d-flex justify-space-between mb-2">
                      <span class="font-weight-medium">{{
                        $t('global.subscription.subscriptionExpires')
                      }}</span>
                      <span>{{ formattedExpirationDate }}</span>
                    </div>
                    <div class="d-flex justify-space-between">
                      <span class="font-weight-medium">{{
                        $t('global.subscription.deviceName')
                      }}</span>
                      <span>{{ deviceName }}</span>
                    </div>
                  </div>

                  <div class="text-center mt-4 underline">
                    <a class="subscription-service-link" :href="localizedLink" target="_blank">
                      {{ $t('global.moreInfoAboutSubscriptionServices') }}
                    </a>
                  </div>
                </v-card-text>
              </v-card>
            </div>

            <v-card v-if="paymentsManagedByEledio" outlined class="renewal-summary-box mb-6">
              <v-card-text class="pa-4">
                <div class="subtitle-1 font-weight-bold">
                  {{ $t('global.subscription.renewByMonths', { months: actualRenewalMonths }) }}
                </div>
                <div class="renewal-info">
                  <div class="subscription-dates mt-4">
                    <div class="d-flex justify-space-between mb-2">
                      <span class="font-weight-medium">{{
                        $t('global.subscription.subscriptionFrom')
                      }}</span>
                      <span>{{ formattedNextStartDate }}</span>
                    </div>
                    <div class="d-flex justify-space-between mb-2">
                      <span class="font-weight-medium">{{
                        $t('global.subscription.subscriptionExpires')
                      }}</span>
                      <span>{{ formattedNextExpirationDate }}</span>
                    </div>
                  </div>

                  <div class="renewal-total mt-4">
                    {{
                      $t('global.subscription.totalPriceIncVat', {
                        price: displayedPrice,
                        currency: selectedCurrency,
                      })
                    }}
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- Pending Transaction Alert -->
            <v-alert
              v-if="hasPendingTransaction && paymentsManagedByEledio"
              type="info"
              class="mb-6"
              border="left"
              colored-border
              icon="mdi-credit-card-clock"
            >
              <div class="d-flex align-center justify-space-between">
                <div>
                  <h3 class="font-weight-bold mb-2">
                    {{ $t('global.transaction.pendingPayment') }}
                  </h3>
                  <p class="mb-0">
                    {{ $t('global.transaction.pendingPaymentDescription') }}
                    <strong
                      >{{ (pendingTransactionData.amountInCents / 100).toFixed(2) }}
                      {{ pendingTransactionData.currency }}</strong
                    >
                  </p>
                </div>
                <v-btn color="primary" @click="completePendingPayment" class="ml-4">
                  <v-icon left>mdi-credit-card</v-icon>
                  {{ $t('global.transaction.completePayment') }}
                </v-btn>
              </div>
            </v-alert>

            <!-- <h2 class="section-title font-weight-bold py-2">
            {{ $t('global.billing.billingInformation') }}
          </h2> -->

            <v-alert v-if="error" type="error" class="mb-4" dismissible @input="error = ''">
              {{ error }}
            </v-alert>

            <!-- Show billing form only in form mode or when no direct mode -->
            <div
              v-if="
                paymentsManagedByEledio &&
                billingInfoManagedByEledio &&
                (isDirectMode || hasPendingTransaction)
              "
            >
              <h2 class="section-title font-weight-bold py-2">
                {{ $t('global.billing.billingInformation') }}
              </h2>

              <v-alert v-if="error" type="error" class="mb-4" dismissible @input="error = ''">
                {{ error }}
              </v-alert>
            </div>

            <!-- Direct Payment Mode - Show summary instead of form -->
            <div v-if="false" class="payment-summary mb-6">
              <h2 class="section-title font-weight-bold py-2">
                {{ $t('global.transaction.paymentSummary') }}
              </h2>

              <v-card outlined class="pa-4">
                <div class="summary-row">
                  <span class="summary-label">{{ $t('global.billing.firstName') }}</span>
                  <span class="summary-value">{{ form.firstName || '-' }}</span>
                </div>
                <div class="summary-row">
                  <span class="summary-label">{{ $t('global.billing.lastName') }}</span>
                  <span class="summary-value">{{ form.lastName || '-' }}</span>
                </div>
                <div class="summary-row">
                  <span class="summary-label">{{ $t('global.billing.company') }}</span>
                  <span class="summary-value">{{ form.company || '-' }}</span>
                </div>
                <div class="summary-row">
                  <span class="summary-label">{{ $t('global.billing.email') }}</span>
                  <span class="summary-value">{{ form.email || '-' }}</span>
                </div>
                <div class="summary-row">
                  <span class="summary-label">{{ $t('global.billing.country') }}</span>
                  <span class="summary-value">{{
                    countryItems.find((c) => c.value === form.country)?.text || form.country || '-'
                  }}</span>
                </div>
              </v-card>
            </div>

            <v-form
              ref="form"
              v-model="valid"
              v-show="paymentsManagedByEledio && (isDirectMode || hasPendingTransaction)"
            >
              <!-- Billing data editing section - v-if (not v-show) so hidden inputs can't block validation -->
              <template v-if="billingInfoManagedByEledio">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.firstName"
                      :label="'* ' + $t('global.billing.firstName')"
                      :readonly="hasPendingTransaction"
                      :error-messages="firstNameErrors"
                      filled
                      rounded
                      dense
                      hide-details="auto"
                      class="mb-4"
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.lastName"
                      :label="'* ' + $t('global.billing.lastName')"
                      :readonly="hasPendingTransaction"
                      :error-messages="lastNameErrors"
                      filled
                      rounded
                      dense
                      hide-details="auto"
                      class="mb-4"
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="form.company"
                      :label="$t('global.billing.company')"
                      :readonly="hasPendingTransaction"
                      filled
                      rounded
                      dense
                      hide-details="auto"
                      class="mb-4"
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.companyId"
                      :label="$t('global.billing.companyId')"
                      filled
                      rounded
                      dense
                      hide-details="auto"
                      class="mb-4"
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.vat"
                      :label="$t('global.billing.vat')"
                      filled
                      rounded
                      dense
                      hide-details="auto"
                      class="mb-4"
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="form.email"
                      :label="'* ' + $t('global.billing.email')"
                      type="email"
                      :error-messages="emailErrors"
                      filled
                      rounded
                      dense
                      hide-details="auto"
                      class="mb-4"
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-row v-if="invalidPrefilledPhone">
                  <v-col cols="12">
                    <v-alert type="warning" outlined border="left" dense>
                      {{ $t('global.billing.invalidSavedPhoneNotice') }}
                    </v-alert>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <v-row no-gutters>
                      <v-col cols="5" sm="4" class="pr-2">
                        <v-text-field
                          ref="phonePrefix"
                          v-model="form.phonePrefix"
                          :label="$t('global.billing.phonePrefix')"
                          :rules="[rules.phonePrefix]"
                          :error-messages="phonePrefixErrors"
                          placeholder="+420"
                          filled
                          rounded
                          dense
                          hide-details="auto"
                          class="mb-4"
                          @focus="showAllErrors"
                          @input="onPhonePrefixChange"
                        />
                      </v-col>
                      <v-col cols="7" sm="8">
                        <v-text-field
                          ref="phoneNumber"
                          v-model="form.phoneNumber"
                          :label="$t('global.billing.phone')"
                          :rules="[rules.phoneNumber]"
                          :error-messages="phoneNumberErrors"
                          placeholder="602123456"
                          filled
                          rounded
                          dense
                          hide-details="auto"
                          class="mb-4"
                          @focus="showAllErrors"
                        />
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <v-select
                      v-model="form.country"
                      :items="countryItems"
                      item-text="text"
                      item-value="value"
                      :label="'* ' + $t('global.billing.country')"
                      :error-messages="countryErrors"
                      @focus="showAllErrors"
                    ></v-select>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.street"
                      :label="$t('global.billing.street')"
                      filled
                      rounded
                      dense
                      hide-details="auto"
                      class="mb-4"
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="form.streetNumber"
                      :label="$t('global.billing.streetNumber')"
                      :error-messages="streetNumberErrors"
                      filled
                      rounded
                      dense
                      hide-details="auto"
                      class="mb-4"
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="form.apartmentNumber"
                      :label="$t('global.billing.apartmentNumber')"
                      :error-messages="apartmentNumberErrors"
                      filled
                      rounded
                      dense
                      hide-details="auto"
                      class="mb-4"
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.zipCode"
                      :label="$t('global.billing.zipCode')"
                      filled
                      rounded
                      dense
                      hide-details="auto"
                      class="mb-4"
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.city"
                      :label="$t('global.billing.city')"
                      filled
                      rounded
                      dense
                      hide-details="auto"
                      class="mb-4"
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- Invoice Language Selection -->
                <v-row>
                  <v-col cols="12">
                    <v-select
                      v-model="form.language"
                      :items="allowedLanguages"
                      item-text="text"
                      item-value="value"
                      :label="$t('global.billing.invoiceLang')"
                      filled
                      rounded
                      dense
                      hide-details="auto"
                      class="mb-4"
                      @focus="showAllErrors"
                    ></v-select>
                  </v-col>
                </v-row>
              </template>

              <!-- Items List -->
              <v-row v-if="billingInfo.items && billingInfo.items.length > 0" class="mb-4">
                <v-col cols="12">
                  <v-card outlined>
                    <v-card-title class="subtitle-1 pb-2">{{
                      $t('global.billing.items')
                    }}</v-card-title>
                    <v-card-text class="pt-0">
                      <div
                        v-for="(item, index) in billingInfo.items"
                        :key="index"
                        class="d-flex justify-space-between align-center py-2"
                      >
                        <span>{{ item.title }}</span>
                        <span class="font-weight-medium"
                          >{{ getItemPrice() }} {{ selectedCurrency }}</span
                        >
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Currency and Price Section - Always visible -->
              <div class="price-section pa-4 mt-4 mb-8 text-center">
                <h3 class="mb-4 font-weight-medium">{{ $t('global.selectedCurrency') }}</h3>
                <div class="currency-buttons">
                  <v-btn-toggle
                    v-model="selectedCurrency"
                    mandatory
                    dense
                    class="currency-toggle"
                    @change="onCurrencyChange"
                  >
                    <v-btn
                      v-for="currency in availableCurrencies"
                      :key="currency"
                      :value="currency"
                      class="currency-btn"
                    >
                      {{ currency }}
                    </v-btn>
                  </v-btn-toggle>
                </div>

                <div v-if="billingInfo.items && billingInfo.items.length > 1" class="mb-2">
                  <div class="text-body-2 grey--text mt-2">
                    {{ getItemPrice() }} {{ selectedCurrency }} ×
                    {{ billingInfo.items.length }} items
                  </div>
                </div>

                <div class="price-display mt-6">
                  <span class="price-label">{{ $t('global.price') }}:</span>
                  <span class="price-value">{{ displayedPrice }} {{ selectedCurrency }}</span>
                </div>
              </div>

              <!-- Subscription Renewal Summary Box -->

              <!-- Submit button -->
            </v-form>

            <!-- Submit button - Outside form so it's always visible -->
            <div v-if="paymentsManagedByEledio" class="submit-container text-center my-8">
              <v-btn
                v-if="hasPendingTransaction"
                color="#0073e6"
                x-large
                rounded
                min-width="200"
                height="56"
                @click="completePendingPayment"
                :disabled="loading"
                class="payment-button white--text"
              >
                <v-icon left size="24">mdi-credit-card</v-icon>
                <span class="font-weight-bold text-h6">{{
                  $t('global.transaction.completePayment')
                }}</span>
              </v-btn>

              <v-btn
                v-else-if="isDirectMode"
                color="#0073e6"
                x-large
                rounded
                min-width="200"
                height="56"
                @click="submitForm"
                :disabled="!valid || loading"
                class="payment-button white--text"
              >
                <v-icon left size="24">mdi-credit-card</v-icon>
                <span class="font-weight-bold text-h6">{{ $t('global.transaction.payNow') }}</span>
              </v-btn>

              <v-btn
                v-else
                color="#0073e6"
                x-large
                rounded
                min-width="200"
                height="56"
                @click="submitForm"
                :disabled="!valid || loading"
                class="payment-button white--text"
              >
                <v-icon left size="24">mdi-credit-card</v-icon>
                <span class="font-weight-bold text-h6">{{ $t('global.confirm') }}</span>
              </v-btn>
            </div>
          </v-card>

          <!-- <div class="footer text-center py-6">
            <p class="copyright">© {{ new Date().getFullYear() }} Eledio</p>
          </div> -->
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { subscriptionRenew } from '@/graphql/mutations/subscriptionRenew'
import { deviceBillingInfo } from '@/graphql/query/deviceBillingInfo'
import { subscriptionStatus } from '@/graphql/query/subscriptionStatus'
import { pendingTransaction } from '@/graphql/query/pendingTransaction'
import {
  countryCodesWithName,
  countryCodesWithNameCs,
  countryCodesWithNamePl,
  countryCodesWithNameDe,
} from '@/config/countryCodes'
import LocaleSelect from '@/components/LocaleSelect'
import NotFoundPage from '@/views/NotFoundPage'
import {
  isPhoneNumber,
  composeE164,
  splitE164,
  normalizePrefix,
} from '@/utils/phoneNumberValidator'
import IXSolveLogo from '@/assets/images/logo_poolmatix_black.svg'

export default {
  name: 'PublicSubscriptionPayment',
  components: {
    LocaleSelect,
    IXSolveLogo,
    NotFoundPage,
  },

  data() {
    return {
      valid: false,
      loading: false,
      error: '',
      deviceName: '',
      subscriptionInfo: null,
      subscriptionData: null,
      pendingTransactionData: null,
      dataLoaded: false,
      billingInfo: {},
      form: {
        firstName: '',
        lastName: '',
        company: '',
        email: '',
        phonePrefix: '',
        phoneNumber: '',
        country: 'CZ',
        street: '',
        streetNumber: '',
        apartmentNumber: '',
        zipCode: '',
        city: '',
        companyId: '',
        vat: '',
        language: 'cze', // Default language for invoice
      },
      invalidPrefilledPhone: false,
      availableCurrencies: ['CZK', 'EUR', 'USD'], // Default currencies
      selectedCurrency: 'CZK', // Default selected currency
      displayedPrice: '0.00', // Price to display based on selected currency
      prices: [], // Store the prices data
      subscriptionLength: 12, // Default subscription length in months
      showErrors: false,
      rules: {
        required: (value) => !!value || this.$t('global.formValidation.required'),
        phonePrefix: (value) => {
          if (!this.form.phoneNumber && !value) return true
          if (!value) return this.$t('global.formValidation.required')
          return /^\+[1-9]\d{0,3}$/.test(value) || this.$t('global.formValidation.notPhoneNumber')
        },
        phoneNumber: (value) => {
          if (!this.form.phonePrefix && !value) return true
          if (!value) return this.$t('global.formValidation.required')
          const full = (this.form.phonePrefix || '') + String(value).replace(/\s+/g, '')
          return isPhoneNumber(full) || this.$t('global.formValidation.notPhoneNumber')
        },
        numeric: (value) => {
          if (!value) return true // Allow empty numeric fields
          const pattern = /^\d+$/
          return pattern.test(value) || this.$t('global.formValidation.notNumeric')
        },
        email: (value) => {
          if (!value) return true // Allow empty email
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || this.$t('global.formValidation.notEmail')
        },
      },
    }
  },

  computed: {
    firstNameErrors() {
      if (!this.showErrors && !this.form.firstName) return []
      return !this.form.firstName ? [this.$t('global.formValidation.required')] : []
    },
    lastNameErrors() {
      if (!this.showErrors && !this.form.lastName) return []
      return !this.form.lastName ? [this.$t('global.formValidation.required')] : []
    },
    emailErrors() {
      if (!this.showErrors && !this.form.email) return []
      const errors = []
      if (!this.form.email) {
        errors.push(this.$t('global.formValidation.required'))
      } else if (this.rules.email(this.form.email) !== true) {
        errors.push(this.$t('global.formValidation.notEmail'))
      }
      return errors
    },
    phonePrefixErrors() {
      if (!this.showErrors && !this.form.phonePrefix && !this.form.phoneNumber) return []
      const res = this.rules.phonePrefix(this.form.phonePrefix)
      return res === true ? [] : [res]
    },
    phoneNumberErrors() {
      if (!this.showErrors && !this.form.phoneNumber && !this.form.phonePrefix) return []
      const res = this.rules.phoneNumber(this.form.phoneNumber)
      return res === true ? [] : [res]
    },
    countryErrors() {
      if (!this.showErrors && !this.form.country) return []
      return !this.form.country ? [this.$t('global.formValidation.required')] : []
    },
    streetNumberErrors() {
      if (!this.form.streetNumber) return []
      return this.rules.numeric(this.form.streetNumber) === true
        ? []
        : [this.$t('global.formValidation.notNumeric')]
    },
    apartmentNumberErrors() {
      if (!this.form.apartmentNumber) return []
      return this.rules.numeric(this.form.apartmentNumber) === true
        ? []
        : [this.$t('global.formValidation.notNumeric')]
    },
    cityErrors() {
      // City is no longer required
      return []
    },
    countryItems() {
      let countryData = countryCodesWithName
      const locale = this.$i18n.locale

      switch (locale) {
        case 'cs':
          countryData = countryCodesWithNameCs
          break
        case 'pl':
          countryData = countryCodesWithNamePl
          break
        case 'de':
          countryData = countryCodesWithNameDe
          break
        default:
          countryData = countryCodesWithName
      }

      return Object.entries(countryData).map(([code, name]) => ({
        text: name,
        value: code,
      }))
    },
    deviceId() {
      return this.$route.params.id
    },
    token() {
      return this.$route.query.token
    },
    mode() {
      return this.$route.query.mode || 'form'
    },
    isDirectMode() {
      return true
    },
    hasPendingTransaction() {
      return !!this.pendingTransactionData
    },
    // Payment (renewal, price, pay buttons) is available only when Eledio manages the payments
    paymentsManagedByEledio() {
      return !!this.billingInfo?.paymentsManagedByEledio
    },
    // The billing data editing section is shown only when Eledio manages the billing info
    billingInfoManagedByEledio() {
      return !!this.billingInfo?.billingInfoManagedByEledio
    },
    // Only once billing info actually arrived - a failed load keeps showing the load error
    showNotFound() {
      return (
        this.dataLoaded &&
        Object.keys(this.billingInfo || {}).length > 0 &&
        !this.paymentsManagedByEledio
      )
    },
    isSubscriptionValid() {
      return this.subscriptionData?.subscriptionStatus?.toLowerCase() === 'valid'
    },
    isSubscriptionExpired() {
      return (
        this.subscriptionData?.subscriptionStatus?.toLowerCase() === 'expired' ||
        this.subscriptionData?.subscriptionStatus?.toLowerCase() === 'unpaid' ||
        this.subscriptionData?.subscriptionStatus?.toLowerCase() === 'blocked'
      )
    },
    formattedStartDate() {
      return this.subscriptionData?.startDate
        ? this.formatDate(parseInt(this.subscriptionData.startDate))
        : '-'
    },
    formattedExpirationDate() {
      return this.subscriptionData?.expirationDate
        ? this.formatDate(parseInt(this.subscriptionData.expirationDate))
        : '-'
    },
    formattedNextStartDate() {
      const expirationDate = this.subscriptionData?.expirationDate
        ? new Date(parseInt(this.subscriptionData.expirationDate))
        : null
      const nextStartDate =
        this.subscriptionData?.subscriptionStatus?.toLowerCase() === 'blocked' || !expirationDate
          ? new Date()
          : new Date(expirationDate)
      if (this.subscriptionData?.subscriptionStatus?.toLowerCase() !== 'blocked') {
        nextStartDate.setDate(nextStartDate.getDate() + 1)
      }
      return this.formatDate(nextStartDate.getTime())
    },
    formattedNextExpirationDate() {
      const expirationDate = this.subscriptionData?.expirationDate
        ? new Date(parseInt(this.subscriptionData.expirationDate))
        : null
      const nextExpirationDate =
        this.subscriptionData?.subscriptionStatus?.toLowerCase() === 'blocked' || !expirationDate
          ? new Date()
          : new Date(expirationDate)
      const monthsToAdd = this.billingInfo?.subscriptionLength || 12
      nextExpirationDate.setMonth(nextExpirationDate.getMonth() + monthsToAdd)
      if (this.subscriptionData?.subscriptionStatus?.toLowerCase() === 'blocked') {
        nextExpirationDate.setDate(nextExpirationDate.getDate() - 1)
      }

      return this.formatDate(nextExpirationDate.getTime())
    },
    localizedLink() {
      const lang = this.$i18n.locale
      return `https://www.eledio.com/${lang}/subscription/`
    },
    daysUntilExpiration() {
      if (!this.subscriptionData?.expirationDate) return 0
      const now = new Date()
      const expirationDate = new Date(parseInt(this.subscriptionData.expirationDate))
      const diffTime = expirationDate - now
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
      return diffDays // Return actual value, can be negative for expired subscriptions
    },
    monthsUntilExpiration() {
      return this.daysUntilExpiration / 30.44 // Average days per month
    },
    actualRenewalMonths() {
      // Calculate actual months based on billing items
      if (this.billingInfo?.items && this.billingInfo.items.length > 0) {
        // Each item represents one subscription period (typically 12 months)
        const baseMonths = this.billingInfo.subscriptionLength || 12
        return this.billingInfo.items.length * baseMonths
      }
      // Default to standard subscription length
      return this.subscriptionLength
    },
    allowedLanguages() {
      return [
        {
          text: this.$t('global.lang.cs'),
          value: 'cze',
        },
        {
          text: this.$t('global.lang.en'),
          value: 'eng',
        },
        {
          text: this.$t('global.lang.de'),
          value: 'deu',
        },
        {
          text: this.$t('global.lang.pl'),
          value: 'pol',
        },
      ]
    },
    showAlreadyPaidMessage() {
      return this.dataLoaded && this.monthsUntilExpiration >= 2
    },
  },

  created() {
    // Set initial language based on URL parameter
    const urlLang = new URLSearchParams(window.location.search).get('lang')
    if (urlLang && ['cs', 'en', 'de', 'pl'].includes(urlLang)) {
      this.$store.commit('changeLocale', urlLang)
    }

    this.fetchData()
  },

  mounted() {
    // Auto-validate fields that have values on mount
    this.$nextTick(() => {
      if (this.hasFormValues()) {
        this.showErrors = true
      }
    })
  },

  watch: {
    '$store.state.locale'() {
      // Only refresh data if form is empty or has no user input
      if (!this.hasFormValues()) {
        this.fetchData()
      }
    },
    selectedCurrency() {
      // Update displayed price when currency changes
      this.updateDisplayedPrice()
    },
    'form.phonePrefix'() {
      const full = composeE164(this.form.phonePrefix, this.form.phoneNumber)
      if (full && isPhoneNumber(full)) this.invalidPrefilledPhone = false
    },
    'form.phoneNumber'() {
      const full = composeE164(this.form.phonePrefix, this.form.phoneNumber)
      if (full && isPhoneNumber(full)) this.invalidPrefilledPhone = false
    },
  },

  methods: {
    onPhonePrefixChange() {
      this.form.phonePrefix = normalizePrefix(this.form.phonePrefix)
      if (this.$refs.phoneNumber && this.$refs.phoneNumber.validate) {
        this.$refs.phoneNumber.validate()
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return '-'

      try {
        const date = new Date(timestamp)
        if (isNaN(date.getTime())) return '-'
        return new Intl.DateTimeFormat(this.$i18n.locale, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(date)
      } catch (error) {
        // Swallow formatting errors and fallback to placeholder
        return '-'
      }
    },

    isMoreThan30DaysLater(timestamp) {
      if (!timestamp) return false
      const now = new Date()
      const futureDate = new Date()
      futureDate.setDate(now.getDate() + 30)
      return parseInt(timestamp) > futureDate.getTime()
    },

    async fetchData() {
      this.loading = true
      try {
        const token = this.token
        const apolloClient = this.$apollo.getClient()

        // Fetch subscription status
        const subscriptionResponse = await apolloClient.query({
          query: subscriptionStatus,
          variables: { deviceId: this.deviceId },
          context: { headers: { 'X-Public-Token': token } },
        })

        if (subscriptionResponse?.data?.subscriptionStatus) {
          this.subscriptionData = subscriptionResponse.data.subscriptionStatus
        }

        // Fetch pending transaction
        try {
          const pendingResponse = await apolloClient.query({
            query: pendingTransaction,
            variables: { deviceId: this.deviceId },
            context: { headers: { 'X-Public-Token': token } },
          })

          if (pendingResponse?.data?.pendingTransaction) {
            this.pendingTransactionData = pendingResponse.data.pendingTransaction
          }
        } catch (pendingError) {
          // Pending transaction query might fail if none exists, that's okay
          // No pending transaction found; proceed without logging to console
        }

        // Fetch billing info
        const billingResponse = await apolloClient.query({
          query: deviceBillingInfo,
          variables: { deviceId: this.deviceId },
          context: { headers: { 'X-Public-Token': token } },
        })

        if (billingResponse?.data?.billingInfo) {
          const billing = billingResponse.data.billingInfo
          this.billingInfo = billing
          const split = splitE164(billing.phone || '')
          this.form = {
            firstName: billing.firstName || '',
            lastName: billing.lastName || '',
            company: billing.company || '',
            email: billing.email || '',
            phonePrefix: split.prefix,
            phoneNumber: split.national,
            country: billing.country || 'CZ',
            street: billing.street || '',
            streetNumber: billing.streetNumber || '',
            apartmentNumber: billing.apartmentNumber || '',
            zipCode: billing.zipCode || '',
            city: billing.city || '',
            companyId: billing.companyId || '',
            vat: billing.vat || '',
            language: billing.invoiceLang || 'cze',
          }

          // If DB had phone but it's not valid, notify user it was omitted
          if (billing.phone && !isPhoneNumber(billing.phone)) {
            this.invalidPrefilledPhone = true
          } else {
            this.invalidPrefilledPhone = false
          }

          // Fetch available currencies from billing info
          this.prices = billing.prices || []
          this.subscriptionLength = billing.subscriptionLength || 12
          this.availableCurrencies = this.prices.map((price) => price.currency)

          // Extract device name
          this.deviceName = billing.deviceName || ''

          if (this.availableCurrencies.length > 0) {
            this.selectedCurrency = this.availableCurrencies[0]
            this.updateDisplayedPrice()
          }
        }
      } catch (error) {
        // Data load error handled via UI state
        this.error = this.$t('global.dataLoadError')
      } finally {
        this.loading = false
        this.dataLoaded = true
      }
    },

    hasFormValues() {
      // Check if form has any user input that shouldn't be overwritten
      return (
        this.form.firstName ||
        this.form.lastName ||
        this.form.company ||
        this.form.phonePrefix ||
        this.form.phoneNumber ||
        this.form.street ||
        this.form.streetNumber ||
        this.form.apartmentNumber ||
        this.form.zipCode ||
        this.form.city ||
        this.form.vat ||
        this.form.companyId
      )
    },
    showAllErrors() {
      this.showErrors = true
    },
    onCurrencyChange(newCurrency) {
      // Ensure we always recompute when the v-model changes
      this.updateDisplayedPrice(newCurrency)
    },
    updateDisplayedPrice(targetCurrency) {
      const current = targetCurrency || this.selectedCurrency
      let priceObj = this.prices.find((p) => p.currency === current)

      // Fallback if selected currency is not available
      if (!priceObj && this.prices.length > 0) {
        priceObj = this.prices[0]
        // Keep UI consistent: align selectedCurrency with the actual price used
        if (priceObj && this.selectedCurrency !== priceObj.currency) {
          this.selectedCurrency = priceObj.currency
        }
      }

      if (priceObj) {
        const rawItemCount = this.billingInfo.items ? this.billingInfo.items.length : 0
        const itemCount = Math.max(1, rawItemCount)
        const totalPrice = priceObj.price * itemCount
        this.displayedPrice = (totalPrice / 100).toFixed(2)
      } else {
        this.displayedPrice = '0.00'
      }
    },

    getItemPrice() {
      // Get the price per item in the selected currency
      let priceObj = this.prices.find((p) => p.currency === this.selectedCurrency)
      if (!priceObj && this.prices.length > 0) {
        priceObj = this.prices[0]
      }
      return priceObj ? (priceObj.price / 100).toFixed(2) : '0.00'
    },

    completePendingPayment() {
      if (this.pendingTransactionData?.paymentUrl) {
        window.location.href = this.pendingTransactionData.paymentUrl
      }
    },

    async submitForm() {
      if (!this.$refs.form.validate()) return

      this.loading = true
      this.error = ''

      try {
        const paymentData = {
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          company: this.form.company,
          phone: composeE164(this.form.phonePrefix, this.form.phoneNumber),
          country: this.form.country,
          street: this.form.street,
          streetNumber: this.form.streetNumber,
          apartmentNumber: this.form.apartmentNumber,
          zipCode: this.form.zipCode,
          city: this.form.city,
          email: this.form.email || '',
          companyId: this.form.companyId || '',
          vat: this.form.vat || '',
          currency: this.selectedCurrency,
          language: this.form.language,
        }

        const apolloClient = this.$apollo.getClient()

        const { data } = await apolloClient.mutate({
          mutation: subscriptionRenew,
          variables: {
            paymentData,
            deviceId: this.deviceId,
            publicAccess: true,
          },
          context: {
            headers: {
              'X-Public-Token': this.token,
            },
          },
        })

        if (data?.initTransaction) {
          const response = data.initTransaction

          if (response.code === '0') {
            // Success - redirect to payment gateway
            window.location.href = response.redirect
          } else {
            // Error response from mutation
            this.error = response.message || this.$t('global.transaction.paymentError')
          }
        } else {
          this.error = this.$t('global.transaction.paymentError')
        }
      } catch (error) {
        // Payment error handled via UI state
        this.error = error.message || this.$t('global.transaction.paymentError')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.fill-height {
  min-height: 100vh;
  width: 100%;
  /* background-color: #f9f9f9; */
  padding: 40px 20px 80px;
  font-family: 'Open Sans', Arial, sans-serif;
}

.eledio-theme {
  --eledio-blue: #0073e6;
  --eledio-text: #333333;
  --eledio-light-gray: #f2f2f2;
  --eledio-border: #e0e0e0;
}

.site-header {
  margin-bottom: 32px;
}

.eledio-logo {
  height: 40px;
}

.payment-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05) !important;
  margin-bottom: 40px;
}
.underline {
  text-decoration: underline;
}
.section-title {
  font-size: 20px;
  color: var(--eledio-text);
  margin-bottom: 24px;
  border-bottom: 1px solid var(--eledio-border);
  padding-bottom: 12px;
}

/* Keeping subscription info section unchanged */
.subscription-details {
  border-left: 4px solid #1976d2 !important;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 16px;
  background: #4caf50;
  color: white;
  font-size: 14px;
  text-align: center;
}

.status-badge.expired {
  background: #f44336;
}

/* Form styling removed - no extra padding needed */

/* Price section styling */
.price-section {
  background-color: var(--eledio-light-gray);
  border-radius: 8px;
}

.currency-toggle {
  display: inline-flex;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.currency-btn {
  padding: 0 16px;
  min-width: 60px;
  background-color: white !important;
  border: 1px solid var(--eledio-border);
}

.currency-btn.v-btn--active {
  background-color: var(--eledio-blue) !important;
  color: white !important;
}

.price-display {
  font-size: 24px;
  font-weight: 500;
}

.price-label {
  margin-right: 12px;
  color: #666;
}

.price-value {
  color: var(--eledio-blue);
  font-size: 28px;
  font-weight: 600;
}

/* Language selector */
.language-selector {
  width: 80px;
}

/* Submit button styling */
.payment-button {
  background-color: var(--eledio-blue) !important;
  box-shadow: 0 4px 10px rgba(0, 115, 230, 0.3) !important;
  transition: transform 0.2s, box-shadow 0.2s !important;
  font-weight: 600 !important;
}

.payment-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(0, 115, 230, 0.4) !important;
}

/* Footer */
.footer {
  color: #666;
  font-size: 14px;
}

.copyright {
  margin: 0;
}

/* Input field styling */
:deep(.v-input--dense .v-input__slot) {
  min-height: 44px !important;
}

:deep(.v-text-field--filled .v-input__slot),
:deep(.v-select--filled .v-input__slot) {
  background-color: var(--eledio-light-gray) !important;
}

:deep(.v-text-field--rounded) {
  border-radius: 8px !important;
}

:deep(.v-label) {
  color: #555 !important;
  font-size: 14px !important;
}

/* Payment Summary Styling */
.payment-summary {
  margin-bottom: 24px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e0e0e0;
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-label {
  font-weight: 500;
  color: #666;
}

.summary-value {
  color: #333;
  font-weight: 600;
}

/* Renewal Summary Box Styling */
.renewal-summary-box {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.renewal-info {
  text-align: center;
}

.renewal-period {
  font-size: 16px;
  font-weight: 500;
  color: var(--eledio-text);
}

.renewal-total {
  font-size: 18px;
  font-weight: 600;
  color: var(--eledio-blue);
}

/* Main Loading Overlay Styling */
.main-loading-overlay {
  z-index: 9999;
}

.loading-content h3 {
  color: var(--eledio-text);
  font-weight: 500;
}

/* Fallback Screen Styling */
.fallback-container {
  min-height: 100vh;
  padding: 40px 20px;
  display: flex;
  align-items: center;
}

.subscription-info-box {
  background-color: var(--eledio-light-gray);
  border-radius: 8px;
  padding: 20px;
  text-align: left;
}

.subscription-info-box p {
  margin: 8px 0;
  color: var(--eledio-text);
}

/* Responsive styling */
@media (max-width: 600px) {
  .fill-height {
    padding: 20px 12px 80px;
  }

  .payment-card {
    padding: 16px !important;
  }

  .price-value {
    font-size: 24px;
  }

  .fallback-container {
    padding: 20px 12px;
  }

  .subscription-info-box {
    padding: 16px;
  }
}
</style>
